// Apple Sign-In wrapper.
//
// - Native (iOS via Capacitor): uses @capacitor-community/apple-sign-in if present.
// - Web: loads Apple's JS SDK (Sign in with Apple JS) on demand and returns
//   the identity token, which the backend verifies against Apple's JWKS.

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const APPLE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const APPLE_REDIRECT_URI = APPLE_ORIGIN ? `${APPLE_ORIGIN}/auth/apple/callback` : "";

//: Lo script di Apple, esposto perché anche la pagina di rientro della popup
//: (`/auth/apple/callback`) deve caricarlo: e' lo script che, girando lì,
//: passa la risposta alla finestra che ha aperto il login. Deve essere lo
//: stesso URL che usa questa pagina, non una seconda copia: due istanze
//: dell'SDK nella stessa finestra non si parlano.
export const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

//: Cosa dice l'utente quando Apple non è disponibile. Vedi la nota sotto.
const APPLE_MISSING_MSG = {
  it: "L'accesso con Apple non è disponibile al momento. Usa l'email o Google.",
  en: "Apple sign-in is not available right now. Use email or Google.",
};

//: **Interruttore unico** dell'accesso con Apple dal browser.
//:
//: `false` finché il Services ID `it.hostaly.app.web` non esiste su Apple
//: Developer (serve l'abbonamento a pagamento e il ruolo Account Holder/Admin):
//: senza quel client la popup risponde «Invalid client», cioè un errore che
//: sembra un guasto del sito. Il pulsante resta visibile ma **spento e grigio**,
//: così la funzione si vede senza prometterla, e la si riaccende cambiando
//: questa riga (la logica sotto è intatta: server, callback e token restano).
//:
//: Non è una variabile d'ambiente di proposito: una build che si comporta
//: diversamente da quello che dice il codice è più difficile da capire di una
//: costante, e qui il valore cambia una volta sola, quando Apple è configurato.
export const APPLE_SIGNIN_ENABLED = false;

//: Perché il pulsante è spento, quando lo è per scelta e non per un guasto.
const APPLE_OFF_MSG = {
  it: "L'accesso con Apple non è ancora attivo: per ora usa l'email o Google.",
  en: "Apple sign-in isn't live yet: use email or Google for now.",
};

//: Apple accetta come `redirectURI` solo Return URL **https registrati** sul
//: Services ID: niente http, niente localhost, niente IP. Da un indirizzo così
//: non è che il login fallisca — è che non può proprio partire, e l'utente
//: legge «login non riuscito» senza nessun indizio. Lo diciamo qui, dove
//: l'informazione c'è: chi prova dal proprio PC (`http://localhost:3000`) o da
//: un sito di prova su un altro dominio sta vedendo questo caso, non un bug.
export const appleOriginSupported = (origin) =>
  /^https:\/\//i.test(String(origin || "")) &&
  !/^https:\/\/(localhost|127\.0\.0\.1|\[::1\])([:\/]|$)/i.test(String(origin || ""));

//: Perché Apple non si può usare **qui**. `null` vuol dire che si può provare.
//: `configured === false` arriva dal server (`GET /api/auth/providers`): sul
//: backend non ci sono le chiavi Apple, quindi la popup si aprirebbe per poi
//: finire in un 503 — dopo che l'utente ha autorizzato.
//:
//: `native: true` salta la regola sull'indirizzo: nell'app iOS il login non
//: passa né dal browser né da un Return URL (lo fa il plugin nativo), e
//: l'origine del WebView (`capacitor://localhost`) non c'entra nulla. Le
//: chiavi sul server però servono anche lì, quindi il primo controllo resta.
export const appleUnavailableReason = ({ configured = true, origin, lang = "it", native = false,
                                         enabled = APPLE_SIGNIN_ENABLED } = {}) => {
  // Prima di tutto l'interruttore: se Apple è spento per scelta, il motivo è
  // quello, e non ha senso raccontare all'utente di un'origine o di una chiave
  // che non c'entrano.
  if (!enabled) return lang === "it" ? APPLE_OFF_MSG.it : APPLE_OFF_MSG.en;
  if (!configured) return lang === "it" ? APPLE_MISSING_MSG.it : APPLE_MISSING_MSG.en;
  if (!native && !appleOriginSupported(origin)) {
    return lang === "it"
      ? "L'accesso con Apple funziona solo su https://hostaly.it: Apple non consente altri indirizzi. Qui usa l'email o Google."
      : "Apple sign-in only works on https://hostaly.it: Apple allows no other address. Use email or Google here.";
  }
  return null;
};

//: Carica lo script una volta sola per finestra. Esposto perché se lo caricassero
//: due moduli diversi sulla stessa pagina (login e popup di rientro) si
//: otterrebbe due istanze dell'SDK, e la seconda sovrascrive la prima.
export const loadAppleSdk = () =>
  new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    if (document.querySelector(`script[src="${APPLE_SDK_SRC}"]`)) {
      // Già in coda di caricamento: si aspetta l'evento invece di accodarne un
      // secondo, che Apple rifiuterebbe come istanza duplicata.
      const esistente = document.querySelector(`script[src="${APPLE_SDK_SRC}"]`);
      esistente.addEventListener("load", () => resolve());
      esistente.addEventListener("error", () => reject(new Error("Apple JS SDK load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = APPLE_SDK_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Apple JS SDK load failed"));
    document.head.appendChild(s);
  });

export async function appleSignIn(lang = "it") {
  // 1. Native path — Capacitor plugin
  //
  // Solo iOS: `ASAuthorizationAppleIDProvider` non esiste su Android e il
  // plugin non ha un'implementazione per quella piattaforma. Dedurlo da
  // `isNativePlatform()` (true anche su Android) mostrerebbe il pulsante Apple
  // nel guscio Android e lo farebbe fallire a ogni tocco.
  const nativo = window.Capacitor?.getPlatform?.() === "ios";
  try {
    if (nativo) {
      // `webpackIgnore` e' obbligatorio: il plugin vive solo nel guscio mobile
      // (`mobile/package.json`), non fra le dipendenze del frontend web, e senza
      // questo commento webpack prova a risolverlo al build e la build CRA
      // muore con "Module not found" — per un ramo che sul web non gira mai.
      // Lasciandolo intatto, l'import viene risolto a runtime dal WebView nativo.
      //
      // Il pacchetto è `@capacitor-community/apple-sign-in`: su iOS il plugin
      // legge **solo** `scopes` (e `state`/`nonce`); `clientId` e `redirectURI`
      // della firma servono alla sua implementazione web, che qui non si usa.
      const mod = await import(/* webpackIgnore: true */ "@capacitor-community/apple-sign-in");
      const res = await mod.SignInWithApple.authorize({
        scopes: "email name",
      });
      const token = res?.response?.identityToken || "";
      const name = [
        res?.response?.givenName,
        res?.response?.familyName,
      ].filter(Boolean).join(" ");
      return { identityToken: token, fullName: name || undefined };
    }
  } catch (e) {
    // Il plugin rifiuta con `localizedDescription`, e su iOS l'annullamento
    // arriva come «The operation couldn't be completed. (…AuthorizationError
    // error 1001.)»: nessun "cancel" da riconoscere, solo il codice 1001 di
    // `ASAuthorizationError.canceled`. Senza quel caso chi tocca «Annulla»
    // legge «Accesso con Apple non disponibile», cioè un errore al posto di
    // una scelta.
    if (String(e?.message || e || "").match(/cancel|1001/i)) {
      return { identityToken: "" }; // user dismissed the sheet
    }
    // Dentro l'app **non** si ricade sul flusso web: la popup di Apple JS sul
    // WebView (`capacitor://localhost`) verrebbe rifiutata per l'indirizzo, e
    // l'utente leggerebbe «login non riuscito» per un motivo che non c'entra.
    // Qui il plugin è l'unica strada: se non risponde, lo si dice.
    if (nativo) {
      throw new Error(lang === "it"
        ? "Accesso con Apple non disponibile in questo momento. Riprova."
        : "Apple sign-in isn't available right now. Try again.");
    }
    // Web: fall through to the JS popup below.
  }

  // 2. Web path — Apple JS SDK popup
  // Prima di aprire la popup: l'indirizzo regge Apple? Vedi `appleOriginSupported`.
  //
  // L'origine si controlla **prima** dell'interruttore, e con `enabled: true`:
  // è un vincolo di Apple (non un indirizzo registrato, non un login
  // possibile), non una scelta del prodotto, e il messaggio deve dire la causa
  // vera. Lo spegnimento è un secondo cancello, subito dopo.
  const motivo = appleUnavailableReason({ configured: true, origin: APPLE_ORIGIN, lang,
                                          enabled: true });
  if (motivo) {
    console.error("[auth] Apple login non tentato: origine non ammessa da Apple.",
                  typeof window !== "undefined" ? window.location.origin : "");
    throw new Error(motivo);
  }
  if (!APPLE_SIGNIN_ENABLED) {
    // Difesa in profondità: il pulsante è già `disabled`, ma questa funzione è
    // esportata e un domani potrebbe essere chiamata da un altro punto.
    console.error("[auth] Apple login spento: APPLE_SIGNIN_ENABLED = false.");
    throw new Error(lang === "it" ? APPLE_OFF_MSG.it : APPLE_OFF_MSG.en);
  }
  if (!APPLE_CLIENT_ID) {
    // Il messaggio che mostra l'utente non può essere il nome della variabile:
    // «Apple login non configurato (REACT_APP_APPLE_CLIENT_ID)» in un toast è
    // robottico, e il nome della variabile finisce in una pagina dove non può
    // servire a nessuno. Il dettaglio tecnico va in console, dove lo legge
    // chi amministra il sito.
    console.error("[auth] Apple login disattivato: REACT_APP_APPLE_CLIENT_ID non impostata alla build.");
    throw new Error(lang === "it" ? APPLE_MISSING_MSG.it : APPLE_MISSING_MSG.en);
  }
  await loadAppleSdk();
  return new Promise((resolve, reject) => {
    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      });
      window.AppleID.auth
        .signIn()
        .then((res) => {
          const n = res?.user?.name || {};
          resolve({
            identityToken: res?.authorization?.id_token || "",
            fullName: [n.givenName, n.familyName].filter(Boolean).join(" ") || undefined,
          });
        })
        .catch((err) => {
          if (String(err?.error || "").match(/cancel/i)) resolve({ identityToken: "" });
          else reject(err);
        });
    } catch (e) {
      reject(e);
    }
  });
}
